import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { Box, Button, IconButton, Stack, Switch, Typography } from "@mui/material";
import { DesktopTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import type { DigestSettingsForm } from "store/types/news";

const btnSx = {
  height: 30,
  borderRadius: "100px",
  border: `1px solid ${"#dbe8f5"}`,
  bgcolor: "#fff",
  color: "#071f35",
  px: "13px",
  fontSize: 12,
  fontWeight: 500,
  textTransform: "none" as const,
  boxShadow: "none",
  "&:hover": { bgcolor: "#f6f8fc", borderColor: "#dbe8f5", boxShadow: "none" }
};

const btnPrimarySx = {
  ...btnSx,
  bgcolor: "#071f35",
  borderColor: "#071f35",
  color: "#fff",
  "&:hover": { bgcolor: "#0a3142", borderColor: "#0a3142", boxShadow: "none" }
};

const btnGhostSx = {
  ...btnSx,
  border: "1px solid transparent",
  bgcolor: "transparent",
  color: "#5d7782",
  "&:hover": { bgcolor: "#f6f8fc", color: "#071f35", borderColor: "transparent", boxShadow: "none" }
};

const ibtnSx = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: `1px solid ${"#dbe8f5"}`,
  bgcolor: "#fff",
  color: "#5d7782",
  "&:hover": { bgcolor: "#f6f8fc" }
};

const toggleSx = {
  width: 36,
  height: 19,
  padding: 0,
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "3px",
    transitionDuration: "180ms",
    "&.Mui-checked": {
      transform: "translateX(17px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#26a6ae",
        opacity: 1,
        border: 0
      }
    }
  },
  "& .MuiSwitch-thumb": {
    width: 13,
    height: 13,
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    bgcolor: "#fff"
  },
  "& .MuiSwitch-track": {
    borderRadius: 100 / 2,
    backgroundColor: "#cdd6de",
    opacity: 1,
    transition: "background-color 180ms"
  }
};

type Props = {
  settings: DigestSettingsForm;
  onSubmit: (next: DigestSettingsForm) => void;
  onCancel: () => void;
};

const SettingRow: React.FC<{
  title: string;
  description: string;
  control: React.ReactNode;
  isLast?: boolean;
}> = ({ title, description, control, isLast }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    gap="14px"
    py="9px"
    borderBottom={isLast ? "none" : "1px solid #dbe8f5"}
  >
    <Stack direction="column" gap="2px" sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#071f35", lineHeight: 1.3 }}>{title}</Typography>
      <Typography sx={{ fontSize: 11, color: "#797979", mt: "2px", lineHeight: 1.4 }}>{description}</Typography>
    </Stack>
    <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
      {control}
    </Stack>
  </Stack>
);

export const DigestSettingsDialog: React.FC<Props> = ({ settings, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState<DigestSettingsForm>(settings);

  return (
    <Box
      sx={{
        width: "min(450px, 94vw)",
        maxWidth: 450,
        minWidth: "min(450px, 94vw)",
        maxHeight: "min(540px, 90vh)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        sx={{
          px: "28px",
          pt: "24px",
          pb: "20px",
          borderBottom: "1px solid #dbe8f5",
          flexShrink: 0
        }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: 18, fontWeight: 500, color: "#071f35", lineHeight: 1.2 }}>
            Digest Settings
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#797979", mt: "2px" }}>
            Personal email delivery preferences
          </Typography>
        </Stack>
        <IconButton aria-label="close" onClick={onCancel} sx={ibtnSx}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      <Stack direction="column" gap="12px" sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: "20px 28px" }}>
        <SettingRow
          title="Enable daily digest email"
          description="Receive an AI-generated summary of your feed each day"
          control={
            <Switch
              sx={toggleSx}
              disableRipple
              checked={draft.digestEmailEnabled}
              onChange={(e) => setDraft((d) => ({ ...d, digestEmailEnabled: e.target.checked }))}
            />
          }
        />
        {Boolean(draft.digestEmailEnabled) && (
          <SettingRow
            title="Delivery time"
            description="Delivered in your local timezone"
            control={
              <DesktopTimePicker
                ampm={false}
                minutesStep={5}
                format="HH:mm"
                value={draft.deliveryTime ? dayjs(`1970-01-01T${draft.deliveryTime}:00`) : null}
                onChange={(next) =>
                  setDraft((d) => ({ ...d, deliveryTime: next ? next.format("HH:mm") : "" }))
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: 120,
                      "& .MuiOutlinedInput-root": {
                        height: 34,
                        borderRadius: "6px",
                        fontSize: 13,
                        color: "#071f35",
                        bgcolor: "#fff",
                        "& fieldset": { borderColor: "#7b92a9" },
                        "&:hover fieldset": { borderColor: "#7b92a9" },
                        "&.Mui-focused fieldset": { borderColor: "#26a6ae", borderWidth: 1 }
                      },
                      "& .MuiOutlinedInput-input": { py: 0, px: "10px" }
                    }
                  }
                }}
              />
            }
          />
        )}
        <SettingRow
          title="Digest content"
          description="Only confirmed entity deals are included (per team setting)"
          control={<Typography sx={{ fontSize: 12, color: "#797979" }}>{draft.digestContentSummary}</Typography>}
        />
        <SettingRow
          title="Use client inbox as source"
          description="Include the Altius shared inbox as an additional news source for your digest"
          control={
            <Switch
              sx={toggleSx}
              disableRipple
              checked={draft.useClientInbox}
              onChange={(e) => setDraft((d) => ({ ...d, useClientInbox: e.target.checked }))}
            />
          }
          isLast={!draft.useClientInbox}
        />
        {Boolean(draft.useClientInbox) && (
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{
              bgcolor: "#e6f7f8",
              borderRadius: "6px",
              mt: "4px",
              p: "10px 12px"
            }}
          >
            <MarkEmailReadIcon sx={{ fontSize: 16, color: "#26a6ae", flexShrink: 0 }} />
            <Stack direction="column" gap="2px" sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#071f35" }}>digest@altius.io</Typography>
              <Typography sx={{ fontSize: 11, color: "#095c62" }}>
                Forward client emails to this address to include them in your digest
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="8px"
        sx={{
          px: "28px",
          py: "16px",
          flexShrink: 0,
          borderTop: "1px solid #dbe8f5"
        }}
      >
        <Button onClick={onCancel} sx={btnGhostSx}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(draft)} sx={btnPrimarySx}>
          Save
        </Button>
      </Stack>
    </Box>
  );
};
